import { Col, Row } from "react-bootstrap";
import {
  CLAIM_SECTIONS,
  fieldContext,
  refCcKey,
  refDisplayKey,
  refEmailKey,
  refPhoneKey,
  visibleFields,
} from "./claimFields.js";
import { claimMailtoHref, claimMailtoTitle } from "../../utils/claimEmail.js";
import { formatDate } from "../../utils/date.js";

// Read-only display of a claim, rendered from CLAIM_SECTIONS. A claim is data-
// heavy, so the default view is compact label/value pairs (Edit switches to the
// form). `claim` is the detail object from getClaim (includes joined *_name).
// Flag sections are editOnly here — ClaimDetailsTab renders them as a banner.

// A contact the office emails ABOUT this claim (1.0 made these header entries
// mailto links, subject pre-filled): the client's email address, the assessor,
// and each assessor-routing contact. The CC rides along from that contact's own
// CC list, which is why Invoices To copies Accounts and Support.
function mailtoValue(field, claim) {
  const text = field.mailto === "ref" ? claim[refDisplayKey(field.name)] : claim[field.name];
  const to = field.mailto === "ref" ? claim[refEmailKey(field.name)] : claim[field.name];
  const cc = field.mailto === "ref" ? claim[refCcKey(field.name)] : null;
  // `field.client` marks the homeowner's own address, which gets 1.0's client
  // subject rather than the reference-laden trade one (utils/claimEmail.js).
  const href = claimMailtoHref(to, claim, cc, { client: !!field.client });
  if (!href) return text || "—"; // no address on file: plain text, never a dead link
  return (
    <a href={href} data-hint={claimMailtoTitle(to, cc)}>
      {text || to}
    </a>
  );
}

// The contact's phone beside their name, read view only (Karen Renall and
// Karen Sweatman, 2026-09-10: "assessor phone number under Assessor"). On the
// SAME line: a second line made the Assessor cell taller and pushed Assessor
// Ref# down the grid (Robert, same day). Plain text, not a tel: link — 1.0's
// field holds "09 966 8241 / 027 505 3503" style pairs, and a link that dials
// half of it is worse than none.
function withPhone(field, claim, node) {
  const phone = field.phone === "ref" ? claim[refPhoneKey(field.name)] : null;
  if (!phone) return node;
  return (
    <>
      {node}
      <span className="small text-muted ms-3 text-nowrap">{phone}</span>
    </>
  );
}

function value(field, claim) {
  if (field.mailto) return withPhone(field, claim, mailtoValue(field, claim));
  if (field.type === "ref") return claim[refDisplayKey(field.name)] || "—";
  if (field.type === "date") return formatDate(claim[field.name], "long") || "—";
  if (field.type === "check") return claim[field.name] ? "Yes" : "—";
  // A radio states BOTH sides, so the read view does too — "—" would throw away
  // the half of the answer that made it a radio rather than a checkbox.
  if (field.type === "radio") {
    return claim[field.name] ? field.onLabel || "Yes" : field.offLabel || "No";
  }
  return claim[field.name] || "—";
}

// Property Excess wears the legacy colour rule (details.md 3.4): while the job
// is Accepted/Completed — red = excess unpaid, orange = unpaid but a not-paid
// reason is recorded; "(Paid)" once the excess invoice is paid.
function excessDisplay(claim) {
  const raw = claim.property_excess;
  const amount = Number(String(raw || "").replace(/[^0-9.]/g, "")) || 0;
  if (!raw) return "—";
  if (claim.excess_paid) return <span>{raw} <span className="text-success">(Paid)</span></span>;
  if (amount > 0 && ["A", "C"].includes(claim.current_status_code)) {
    const cls = claim.excess_not_paid_reason ? "text-warning" : "text-danger";
    const why = claim.excess_not_paid_reason ? ` — ${claim.excess_not_paid_reason}` : " — not paid";
    return <span className={cls}>{raw}{why}</span>;
  }
  return raw;
}

function ClaimView({ claim }) {
  const ctx = fieldContext({ claim });
  return (
    <>
      {/* Hierarchy: section headers OUTRANK field labels — small-caps semibold
          headers, quiet plain-muted labels, so the eye lands on groups + values. */}
      {CLAIM_SECTIONS.filter((s) => !s.editOnly).map((section) => {
        const fields = visibleFields(section, ctx);
        if (fields.length === 0) return null;
        const body = (
          <Row className="g-2">
            {fields.map((field) => (
              <Col xs={12} md={field.wide ? 12 : 6} key={field.name}>
                <div className="d-flex">
                  <div className="text-muted me-2" style={{ minWidth: 150 }}>
                    {field.label}
                  </div>
                  <div className="flex-grow-1" style={{ whiteSpace: "pre-wrap" }}>
                    {field.name === "property_excess" ? excessDisplay(claim) : value(field, claim)}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        );
        // Every section collapses, and only Assessor Routing starts shut. This
        // is the print control (Robert 2026-08-13): a section the user closes
        // does not print AT ALL, header included — see styles/print.css. So
        // "which sections go on this job sheet" is answered by the same
        // triangles they use to shorten the screen.
        //
        // `open` is passed as an INITIAL state, not a controlled one: React
        // writes the attribute only when the prop value changes between
        // renders, and it never does here, so a section the user shuts stays
        // shut through re-renders. It reopens on remount (navigating away and
        // back), which is the accepted cost of not tracking a dozen toggles in
        // state. If it ever needs to survive that, persist it per user.
        return (
          <details
            key={section.title}
            className="mb-3 p-3 claim-section"
            open={!section.startClosed}
          >
            {/* Same heading class both ways — see .claim-section-title in
                app.css for why a <summary> needs telling what an <h6> is given. */}
            <summary className="claim-section-title text-uppercase fw-semibold small border-bottom pb-1 mb-2">
              {section.title}
            </summary>
            {body}
          </details>
        );
      })}
    </>
  );
}

export default ClaimView;
